import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, Bot, Hash, BarChart3, FileText, DollarSign, Settings, Key, LogOut, Shield, Menu, X, Zap, Sparkles, Download } from 'lucide-react';
import NotificationCenter from './NotificationCenter';
import { useAuth } from '../context/AuthContext';
import { fetchPublicPlans, findPlan, formatPlanSummary } from '../lib/planCatalog';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/accounts', icon: Users, label: 'Accounts' },
  { path: '/my-eas', icon: Bot, label: 'My EAs' },
  { path: '/magic-numbers', icon: Hash, label: 'Magic Numbers' },
  { path: '/trades', icon: BarChart3, label: 'Trades' },
  { path: '/reports', icon: FileText, label: 'Reports' },
  { path: '/expenses', icon: DollarSign, label: 'Expenses' },
];

const bottomItems = [
  { path: '/settings/ea-download', icon: Download, label: 'Download EA' },
  { path: '/settings/api-keys', icon: Key, label: 'API Keys' },
  { path: '/settings', icon: Settings, label: 'Settings' },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [plans, setPlans] = useState([]);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);
  useEffect(() => {
    fetchPublicPlans()
      .then(setPlans)
      .catch(() => {});
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };
  const initials = user?.username ? user.username.slice(0, 2).toUpperCase() : 'U';
  const effectivePlan = user?.effectivePlan || user?.plan || 'free';
  const proPlan = findPlan(plans, 'pro');
  const upgradeSummary = formatPlanSummary(proPlan);

  const sidebarContent = (
    <div className="flex flex-col h-full relative">
      {/* Ambient gradient orbs */}
      <div className="absolute top-8 left-1/2 -translate-x-1/2 w-40 h-40 rounded-full opacity-[0.06] blur-3xl pointer-events-none" style={{ background: 'var(--accent-primary)' }} />
      <div className="absolute bottom-20 left-1/4 w-24 h-24 rounded-full opacity-[0.04] blur-2xl pointer-events-none" style={{ background: 'var(--accent-secondary)' }} />

      {/* User Profile */}
      <div className="px-4 pt-6 pb-4">
        <div className="flex items-center gap-3 p-3 rounded-xl transition-all duration-300 hover:bg-[var(--bg-card-hover)]" style={{ background: 'rgba(255,255,255,0.02)' }}>
          <div className="relative">
            <div className="w-10 h-10 rounded-full p-[2px]" style={{ background: 'var(--gradient-primary)' }}>
              <div className="w-full h-full rounded-full flex items-center justify-center" style={{ background: 'var(--bg-deepest)' }}>
                <span className="text-sm font-bold" style={{ color: 'var(--accent-primary)' }}>{initials}</span>
              </div>
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2" style={{ borderColor: 'var(--bg-deepest)' }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-[var(--text-primary)] truncate">{user?.username || 'User'}</p>
            <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase px-2 py-0.5 rounded-full mt-0.5 ${
              effectivePlan === 'pro' || effectivePlan === 'trial'
                ? 'text-amber-400 border border-amber-500/30'
                : 'text-[var(--text-dim)] border border-[var(--border-color)]'
            }`} style={{
              background: effectivePlan === 'pro' || effectivePlan === 'trial' ? 'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(249,115,22,0.1))' : 'rgba(255,255,255,0.03)',
            }}>
              {(effectivePlan === 'pro' || effectivePlan === 'trial') && <Zap size={8} />}
              {effectivePlan}
            </span>
          </div>
          <NotificationCenter />
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
        {navItems.map(({ path, icon: Icon, label }) => (
          <NavLink
            key={path}
            to={path}
            className={({ isActive }) =>
              `group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all duration-200 ${
                isActive
                  ? 'text-[var(--accent-primary)]'
                  : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`
            }
          >
            {({ isActive }) => (
              <>
                {/* Active background glow */}
                {isActive && (
                  <div className="absolute inset-0 rounded-xl opacity-100" style={{
                    background: `linear-gradient(135deg, color-mix(in srgb, var(--accent-primary) 10%, transparent), color-mix(in srgb, var(--accent-primary) 4%, transparent))`,
                    border: '1px solid color-mix(in srgb, var(--accent-primary) 20%, transparent)',
                  }} />
                )}
                {/* Hover background */}
                {!isActive && (
                  <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200" style={{ background: 'rgba(255,255,255,0.03)' }} />
                )}
                {/* Active left indicator */}
                {isActive && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-full" style={{
                    background: 'var(--accent-primary)',
                    boxShadow: `0 0 10px var(--accent-primary)`,
                  }} />
                )}
                <Icon size={17} className={`relative z-10 transition-colors ${isActive ? 'text-[var(--accent-primary)]' : 'text-[var(--text-dim)] group-hover:text-[var(--text-secondary)]'}`} />
                <span className="relative z-10">{label}</span>
                {isActive && (
                  <div className="relative z-10 ml-auto w-1.5 h-1.5 rounded-full" style={{
                    background: 'var(--accent-primary)',
                    boxShadow: `0 0 8px var(--accent-primary)`,
                  }} />
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Bottom Section */}
      <div className="px-3 pt-3 pb-2 space-y-0.5" style={{ borderTop: '1px solid var(--border-color)' }}>
        {/* Admin Link */}
        {user?.role === 'admin' && (
          <NavLink to="/admin" className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-xl text-[12px] font-medium transition-all duration-200 ${
              isActive ? 'bg-red-500/10 text-red-400' : 'text-red-400/50 hover:text-red-400 hover:bg-red-500/5'
            }`
          }>
            <Shield size={15} />
            <span>Admin Panel</span>
          </NavLink>
        )}
        {bottomItems.map(({ path, icon: Icon, label }) => (
          <NavLink
            key={path}
            to={path}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-xl text-[12px] font-medium transition-all duration-200 ${
                isActive
                  ? 'text-[var(--accent-primary)] bg-[color-mix(in_srgb,var(--accent-primary)_6%,transparent)]'
                  : 'text-[var(--text-dim)] hover:text-[var(--text-secondary)] hover:bg-[rgba(255,255,255,0.03)]'
              }`
            }
          >
            <Icon size={15} />
            <span>{label}</span>
          </NavLink>
        ))}
        <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-[12px] font-medium text-[var(--text-dim)] hover:text-red-400 hover:bg-red-400/5 transition-all duration-200">
          <LogOut size={15} />
          <span>Logout</span>
        </button>
      </div>

      {/* Upgrade CTA — free users */}
      {effectivePlan !== 'pro' && effectivePlan !== 'trial' && (
        <div
          className="mx-3 mb-4 mt-2 p-4 rounded-xl relative overflow-hidden group cursor-pointer"
          onClick={() => navigate('/settings/upgrade')}
          style={{
            background: 'linear-gradient(135deg, color-mix(in srgb, var(--accent-primary) 5%, transparent), color-mix(in srgb, var(--accent-secondary) 3%, transparent))',
            border: '1px dashed color-mix(in srgb, var(--accent-primary) 25%, transparent)',
          }}>
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
            background: 'linear-gradient(135deg, color-mix(in srgb, var(--accent-primary) 8%, transparent), transparent)',
          }} />
          <div className="relative flex items-center gap-2 mb-2">
            <Sparkles size={14} style={{ color: 'var(--accent-primary)' }} />
            <span className="text-xs font-semibold text-[var(--text-primary)]">Upgrade to Pro</span>
          </div>
          <p className="relative text-[10px] text-[var(--text-dim)] mb-3 leading-relaxed">{upgradeSummary}.</p>
          <button className="relative w-full py-2 rounded-lg text-[11px] font-bold text-[var(--bg-deepest)] transition-all duration-200 hover:shadow-lg" style={{
            background: 'var(--gradient-primary)',
            boxShadow: '0 4px 12px var(--shadow-glow)',
          }}>
            Upgrade →
          </button>
        </div>
      )}

      {/* Mobile close */}
      <button onClick={() => setMobileOpen(false)} className="lg:hidden absolute top-4 right-4 p-1.5 text-[var(--text-dim)] hover:text-[var(--text-primary)] rounded-lg hover:bg-[rgba(255,255,255,0.05)]">
        <X size={18} />
      </button>
    </div>
  );

  return (
    <>
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 flex items-center justify-between px-4 z-40" style={{
        background: 'var(--sidebar-bg, rgba(10,13,31,0.95))',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '1px solid var(--border-color)',
      }}>
        <button onClick={() => setMobileOpen(true)} className="p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] rounded-lg">
          <Menu size={20} />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--gradient-primary)' }}>
            <BarChart3 size={14} className="text-[var(--bg-deepest)]" />
          </div>
          <span className="font-display font-bold text-sm" style={{ color: 'var(--accent-primary)' }}>TrackMyFxEA</span>
        </div>
        <div className="w-8" />
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed left-3 top-3 bottom-3 w-[220px] flex-col z-50 overflow-hidden rounded-2xl" style={{
        background: 'var(--sidebar-bg, rgba(10,13,31,0.95))',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid var(--border-color)',
        boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
      }}>
        {sidebarContent}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-[280px] animate-slide-in overflow-y-auto" style={{
            background: 'var(--sidebar-bg, rgba(10,13,31,0.98))',
            borderRight: '1px solid var(--border-color)',
          }}>
            {sidebarContent}
          </aside>
        </div>
      )}
    </>
  );
}
